"use client";

// ** React Imports
import React, { memo } from "react";

// ** Third Party Components
import { ErrorBoundary } from "react-error-boundary";
// import loadable from "@loadable/component";

// ** Components Imports
// const FallbackContent = loadable(() => import("./components/FallbackContent"), {
// 	ssr: false,
// });

// ** Utils & Helpers
import api from "helpers/ApiClient";
import FallbackContent from "./components/FallbackContent";

// #####################################################

const SEND_API_ERROR = true;
const FALLBACK_RELOAD_DELAY_APPLICATION = 10 * 1000;
const FALLBACK_RELOAD_DELAY_MISSING_CHUNK = 3 * 1000;

// #####################################################

// ** Funkcja pomocnicza do klasyfikacji błędów DOM
const classifyDOMError = (error) => {
	const errorMessage = error?.message || "";
	const errorStack = error?.stack || "";

	// Błędy związane z manipulacją DOM
	if (
		errorMessage.includes("removeChild") ||
		errorMessage.includes("The object can not be found here") ||
		errorStack.includes("removeChild")
	) {
		return {
			type: "DOM_MANIPULATION_ERROR",
			category: "removeChild",
			description: "Próba usunięcia elementu DOM który już nie istnieje",
		};
	}

	// Inne klasyfikacje błędów DOM
	if (
		errorMessage.includes("appendChild") ||
		errorStack.includes("appendChild")
	) {
		return {
			type: "DOM_MANIPULATION_ERROR",
			category: "appendChild",
			description: "Błąd podczas dodawania elementu do DOM",
		};
	}

	return {
		type: "UNKNOWN_ERROR",
		category: "other",
		description: "Nieznany typ błędu",
	};
};

const Fallback = ({ children }) => {
	const isDev = process.env.NODE_ENV === "development";

	// ** Funkcja sprawdzająca czy strona była już odświeżana
	const checkIsAfterRefresh = () => {
		if (typeof window !== "undefined") {
			return window.location.hash.includes("#refresh");
		}
		return false;
	};

	const reloadPage = (fromButton = false) => {
		if (checkIsAfterRefresh() && !fromButton) {
			return;
		}

		if (typeof window !== "undefined") {
			const currentUrl = new URL(window.location.href);
			currentUrl.hash = "refresh";
			window.location.href = currentUrl.toString();
			window.location.reload();
		}
	};

	const fallbackRender = ({ error }) => {
		const missingChunkType =
			error.type === "missing" ||
			error.name === "ChunkLoadError" ||
			error.code === "CSS_CHUNK_LOAD_FAILED"; // ** Poprawka #3: Zmiana == na ===
		return (
			<FallbackContent
				missingChunkType={missingChunkType}
				reloadPage={reloadPage}
			/>
		);
	};

	const handleError = (error, info) => {
		if (
			error.type === "missing" ||
			error.name === "ChunkLoadError" ||
			error.code === "CSS_CHUNK_LOAD_FAILED" // ** Poprawka #3: Zmiana == na ===
		) {
			setTimeout(() => {
				if (typeof window !== "undefined") {
					reloadPage();
				}
			}, FALLBACK_RELOAD_DELAY_MISSING_CHUNK);
		} else {
			if (!isDev) {
				if (SEND_API_ERROR && !checkIsAfterRefresh()) {
					const domErrorInfo = classifyDOMError(error);

					const data = {
						route: window?.location?.href ?? "FAILED_TO_GET_ROUTE",
						error: error?.message ?? "UNKNOWN ERROR",
						error_stack: error?.stack?.toString() ?? "",
						from: "Fallback",
						error_full: JSON.stringify(error),
						error_info: JSON.stringify(info),
						// Dodatkowe informacje diagnostyczne
						dom_error_type: domErrorInfo.type,
						dom_error_category: domErrorInfo.category,
						dom_error_description: domErrorInfo.description,
					};
					api.post("/system/react-error", data);
				}

				setTimeout(() => {
					reloadPage();
				}, FALLBACK_RELOAD_DELAY_APPLICATION);
			}
		}
	};

	return (
		<ErrorBoundary fallbackRender={fallbackRender} onError={handleError}>
			{children}
		</ErrorBoundary>
	);
};

export default memo(Fallback);
